<template>
  <form @submit.prevent="handleSubmit" class="Restaurant-filters">
    <div class="Restaurant-filter__error" v-if="displayErrorMessage">
      <ux-atoms-icon name="error" size="m" />
      <ux-atoms-typo :text="$t('ux.molecules.restaurantFilters.error')" as="p" variant="text-medium" />
    </div>
    <div class="Restaurant-filters__container">
      <ux-atoms-link href="#after-price-selection" skip v-if="filterData?.globalMaxPrice && filterData?.globalMinPrice && displayPriceFilter">
        {{ $t('ux.molecules.restaurantFilters.nextFilter', { filter: $t('ux.molecules.restaurantFilters.priceSelection') }) }}
      </ux-atoms-link>
      <div class="Restaurant-filters__sub-container" v-if="filterData?.globalMaxPrice && filterData?.globalMinPrice && displayPriceFilter">
        <ux-atoms-typo :text="$t('ux.molecules.restaurantFilters.priceSelection')" as="h3" variant="text-heading-01" />
        <ux-atoms-form-double-range
          :currency="filterDataCurrency"
          :default-current-max="defaultCurrentMax as number"
          :default-current-min="defaultCurrentMin as number"
          :disabled="disabled"
          :max="maxPriceRange as number"
          :min="minPriceRange as number"
          ref="doubleRange"
          v-if="filterInput.maxPrice && filterInput.minPrice"
          v-model:modelCurrentMax="filterInput.maxPrice"
          v-model:modelCurrentMin="filterInput.minPrice"
        />
      </div>

      <span class="accessibility-sr-only" id="after-price-selection" v-if="filterData?.globalMaxPrice && filterData?.globalMinPrice"></span>
      <ux-atoms-link href="#after-availability" skip v-if="displayCriteria">
        {{ $t('ux.molecules.restaurantFilters.nextFilter', { filter: $t('ux.molecules.restaurantFilters.restaurantsAvailability') }) }}
      </ux-atoms-link>

      <div class="Restaurant-filters__sub-container" v-if="displayCriteria">
        <ux-atoms-typo :text="$t('ux.molecules.restaurantFilters.criteriaSelection')" as="h3" variant="text-heading-01" />
        <ux-atoms-form-field-set :legend="$t('ux.molecules.restaurantFilters.restaurantsAvailability')">
          <ux-atoms-form-checkbox-input
            :disabled="disabled || filterData?.available === 0"
            :label="$t('ux.molecules.restaurantFilters.restaurantsAvailabilityLabel', { count: filterData?.available })"
            :sr-theme="$t('ux.molecules.restaurantFilters.restaurantsAvailability')"
            :value="filterData?.available || 0"
            v-model="filterInput.available as boolean"
          />
        </ux-atoms-form-field-set>

        <span class="accessibility-sr-only" id="after-availability" v-if="displayCriteria"></span>
        <ux-atoms-link href="#after-thematics" skip>
          {{ $t('ux.molecules.restaurantFilters.nextFilter', { filter: $t('ux.molecules.restaurantFilters.thematics') }) }}
        </ux-atoms-link>

        <ux-atoms-form-field-set :legend="$t('ux.molecules.restaurantFilters.thematics')" v-if="filterData?.THEMATIC?.length">
          <ul class="Restaurant-filters__input-list">
            <li :key="param?.code as string" class="Restaurant-filters__input-list-container" v-for="param in filterData?.THEMATIC">
              <ux-atoms-form-checkbox-input
                :disabled="disabled || param?.count === 0"
                :label="`${param?.code ? t(`ux.molecules.cardRestaurant.mainThematics.${param.code}`) : param?.label} (${param?.count})`"
                :sr-theme="$t('ux.molecules.restaurantFilters.thematics')"
                :value="param?.code as string"
                v-model="filterInput.THEMATIC as string[]"
              />
            </li>
          </ul>
        </ux-atoms-form-field-set>

        <span class="accessibility-sr-only" id="after-thematics" v-if="displayCriteria"></span>
        <ux-atoms-link href="#after-style-of-food" skip>
          {{ $t('ux.molecules.restaurantFilters.nextFilter', { filter: $t('ux.molecules.restaurantFilters.styleOfFood') }) }}
        </ux-atoms-link>

        <ux-atoms-form-field-set :legend="$t('ux.molecules.restaurantFilters.styleOfFood')" class="Restaurant-filters__fieldset">
          <ul class="Restaurant-filters__input-list">
            <li :key="param?.code as string" class="Restaurant-filters__input-list-container" v-for="param in filterData?.STYLE_OF_FOOD">
              <ux-atoms-form-checkbox-input
                :disabled="disabled || param?.count === 0"
                :label="`${param?.label} (${param?.count})`"
                :sr-theme="$t('ux.molecules.restaurantFilters.styleOfFood')"
                :value="param?.code"
                v-if="filterData?.STYLE_OF_FOOD && filterInput.STYLE_OF_FOOD && param && param.code"
                v-model="filterInput.STYLE_OF_FOOD"
              />
            </li>
          </ul>
        </ux-atoms-form-field-set>

        <span class="accessibility-sr-only" id="after-style-of-food"></span>
        <ux-atoms-link href="#after-food-preferences" skip v-if="filterData?.FOOD_PREFERENCES?.length">
          {{ $t('ux.molecules.restaurantFilters.nextFilter', { filter: $t('ux.molecules.restaurantFilters.foodPreferences') }) }}
        </ux-atoms-link>

        <ux-atoms-form-field-set
          :legend="$t('ux.molecules.restaurantFilters.foodPreferences')"
          class="Restaurant-filters__fieldset"
          v-if="filterData?.FOOD_PREFERENCES?.length"
        >
          <ul class="Restaurant-filters__input-list">
            <li :key="param?.code as string" class="Restaurant-filters__input-list-container" v-for="param in FoodPreferencesDataComputed">
              <ux-atoms-form-checkbox-input
                :disabled="disabled || param?.count === 0"
                :label="`${param?.label} (${param?.count})`"
                :sr-theme="$t('ux.molecules.restaurantFilters.foodPreferences')"
                :value="param?.code"
                v-if="filterData.FOOD_PREFERENCES && filterInput.FOOD_PREFERENCES && param && param.code"
                v-model="filterInput.FOOD_PREFERENCES"
              />
            </li>
          </ul>
        </ux-atoms-form-field-set>

        <span class="accessibility-sr-only" id="after-food-preferences" v-if="filterData?.FOOD_PREFERENCES?.length"></span>
        <ux-atoms-link href="#after-customer-ratings" skip v-if="filterData?.AVERAGE_RATING?.length">
          {{ $t('ux.molecules.restaurantFilters.nextFilter', { filter: $t('ux.molecules.restaurantFilters.customerRatings') }) }}
        </ux-atoms-link>

        <ux-atoms-form-field-set
          :legend="$t('ux.molecules.restaurantFilters.customerRatings')"
          class="Restaurant-filters__fieldset"
          v-if="displayAverageRating"
        >
          <ul class="Restaurant-filters__input-list Restaurant-filters__input-list--singleCol">
            <li :key="param?.code as string" class="Restaurant-filters__input-list-container" v-for="param in filterData?.AVERAGE_RATING">
              <ux-atoms-form-checkbox-input
                :disabled="disabled || param?.count === 0"
                :value="param?.code"
                v-if="filterData.AVERAGE_RATING && filterInput.AVERAGE_RATING && param && param.code"
                v-model="filterInput.AVERAGE_RATING"
              >
                <span class="Restaurant-filters__input-list-container__label">
                  <ux-atoms-rating :stars="param.code === 'FOUR_PLUS' ? 4 : 3" :variant="'filter' as RatingVariant" />
                  {{ $t('ux.molecules.restaurantFilters.andMore', { count: param.code === 'FOUR_PLUS' ? 4 : 3 }) }} ({{ param?.count }})
                </span>
              </ux-atoms-form-checkbox-input>
            </li>
          </ul>
        </ux-atoms-form-field-set>

        <span class="accessibility-sr-only" id="after-customer-ratings" v-if="filterData?.AVERAGE_RATING?.length"></span>
        <ux-atoms-link href="#after-offers-loyalty" skip v-if="filterData?.OFFER_AND_LOYALTY?.length">
          {{ $t('ux.molecules.restaurantFilters.nextFilter', { filter: $t('ux.molecules.restaurantFilters.offersLoyalty') }) }}
        </ux-atoms-link>

        <ux-atoms-form-field-set
          :legend="$t('ux.molecules.restaurantFilters.offersLoyalty')"
          class="Restaurant-filters__fieldset"
          v-if="displayOfferAndLoyalty"
        >
          <ul class="Restaurant-filters__input-list">
            <li :key="param?.code as string" class="Restaurant-filters__input-list-container" v-for="param in offerAndLoyalty">
              <ux-atoms-form-checkbox-input
                :disabled="disabled || param?.count === 0"
                :label="`${param?.label} (${param?.count})`"
                :sr-theme="$t('ux.molecules.restaurantFilters.offersLoyalty')"
                :value="param?.code"
                v-if="filterData.OFFER_AND_LOYALTY && filterInput.OFFER_AND_LOYALTY && param && param.code && param.count && param.count > 0"
                v-model="filterInput.OFFER_AND_LOYALTY"
              />
            </li>
          </ul>
        </ux-atoms-form-field-set>

        <span class="accessibility-sr-only" id="after-offers-loyalty" v-if="filterData?.OFFER_AND_LOYALTY?.length"></span>
      </div>
    </div>

    <div class="Restaurant-filters__controls">
      <ux-atoms-button
        :label="$t('ux.molecules.restaurantFilters.clear')"
        @click="clearFilterInput"
        class="Restaurant-filters__button Restaurant-filters__button--clear"
        variant="link"
      />
      <ux-atoms-button
        :disabled="disabled"
        :label="$t('ux.molecules.restaurantFilters.showResults')"
        @click="handleSubmit"
        class="Restaurant-filters__button Restaurant-filters__button--submit"
        size="m"
        variant="tertiary"
      />
    </div>
  </form>
</template>
<script lang="ts" setup>
import { RatingVariant } from '~/core';
import { gtmEvent } from '~/helpers/gtm';

export interface RestaurantFiltersProps {
  currentView: 'list' | 'map';
}

const props = withDefaults(defineProps<RestaurantFiltersProps>(), {
  currentView: 'map'
});

const {
  dataComputed: filterData,
  displayCriteria,
  displayErrorMessage,
  filterInput,
  handleInitialFilterDataInput,
  inputStatus,
  reset,
  submitFilterInput,
  updateInputStatus
} = searchRestaurantStore();

const disabled = computed(() => inputStatus.state);

const doubleRange = ref();
const { t } = useI18n();

const filterDataCurrency = computed(() => filterData.value?.currency ?? 'EUR');

const defaultCurrentMax = computed(() => filterInput?.maxPrice);
const defaultCurrentMin = computed(() => filterInput?.minPrice);

const displayAverageRating = computed(() => filterData?.value?.AVERAGE_RATING?.some((el) => el && el?.count && el?.count > 0));
const displayOfferAndLoyalty = computed(() => filterData?.value?.OFFER_AND_LOYALTY?.some((el) => el && el?.count && el?.count > 0));

const displayPriceFilter = computed(() => filterData?.value?.available && filterData?.value?.available > 2);

const maxPriceRange = computed(() => filterData.value?.globalMaxPrice);
const minPriceRange = computed(() => filterData.value?.globalMinPrice);

const offerAndLoyalty = computed(() => filterData?.value?.OFFER_AND_LOYALTY?.filter((el) => el && el.count && el?.count > 0));
const FoodPreferencesDataComputed = computed(() =>
  filterData?.value?.FOOD_PREFERENCES?.filter(
    (el) => el?.code !== 'br' || (el.code === 'br' && !filterData.value?.THEMATIC?.find((el) => el?.code === 'br'))
  )
);

const submitEvent = () => {
  if (
    (filterInput.maxPrice && filterData.value?.globalMaxPrice !== filterInput.maxPrice) ||
    (filterInput.minPrice && filterData.value?.globalMinPrice !== filterInput.minPrice)
  ) {
    gtmEvent('more_criteria_open', {
      filter_label: t('ux.molecules.restaurantFilters.priceSelection'),
      filter_name: t('ux.molecules.restaurantFilters.priceSelection'),
      pagename: `restaurantsandbars::results::${props.currentView}`
    });
  }

  if (filterInput.available) {
    gtmEvent('more_criteria_open', {
      filter_label: t('ux.molecules.restaurantFilters.restaurantsAvailabilityLabel'),
      filter_name: t('ux.molecules.restaurantFilters.restaurantsAvailability'),
      pagename: `restaurantsandbars::results::${props.currentView}`
    });
  }

  filterInput.FOOD_PREFERENCES?.forEach((input: string) => {
    const filter = filterData.value?.FOOD_PREFERENCES?.find((o) => {
      return o?.code === input;
    });

    gtmEvent('more_criteria_open', {
      filter_label: filter?.label,
      filter_name: t('ux.molecules.restaurantFilters.foodPreferences'),
      pagename: `restaurantsandbars::results::${props.currentView}`
    });
  });

  filterInput.STYLE_OF_FOOD?.forEach((input: string) => {
    const filter = filterData.value?.STYLE_OF_FOOD?.find((o) => {
      return o?.code === input;
    });

    gtmEvent('more_criteria_open', {
      filter_label: filter?.label,
      filter_name: t('ux.molecules.restaurantFilters.styleOfFood'),
      pagename: `restaurantsandbars::results::${props.currentView}`
    });
  });

  filterInput.THEMATIC?.forEach((input: string) => {
    const filter = filterData.value?.THEMATIC?.find((o) => {
      return o?.code === input;
    });

    gtmEvent('more_criteria_open', {
      filter_label: filter?.label,
      filter_name: t('ux.molecules.restaurantFilters.thematics'),
      pagename: `restaurantsandbars::results::${props.currentView}`
    });
  });

  filterInput.AVERAGE_RATING?.forEach((input: string) => {
    const filter = filterData.value?.AVERAGE_RATING?.find((o) => {
      return o?.code === input;
    });

    gtmEvent('bloc_interact', {
      filter_label: filter?.label,
      filter_name: t('ux.molecules.restaurantFilters.customerRatings'),
      pagename: `restaurantsandbars::results::${props.currentView}`
    });
  });

  filterInput.OFFER_AND_LOYALTY?.forEach((input: string) => {
    const filter = filterData.value?.OFFER_AND_LOYALTY?.find((o) => {
      return o?.code === input;
    });

    gtmEvent('bloc_interact', {
      filter_label: filter?.label,
      filter_name: t('ux.molecules.restaurantFilters.offersLoyalty'),
      pagename: `restaurantsandbars::results::${props.currentView}`
    });
  });
};

const handleSubmit = () => {
  submitEvent();
  submitFilterInput();
};

const clearFilterInput = () => {
  reset();
  doubleRange.value.clearInput();
};

const handleFilterInput = () => {
  updateInputStatus(true);
};

watch(filterInput, () => {
  handleFilterInput();
});

onBeforeMount(() => {
  nextTick(() => {
    handleInitialFilterDataInput();
  });
});
</script>
<style lang="scss" scoped>
@use 'RestaurantFilters.scss';
</style>
